import React, { useRef, useEffect, useState } from 'react';
import { Edit3, RotateCcw, Check, CheckCircle2 } from 'lucide-react';

interface SignaturePadProps {
  label?: string;
  signatureDataUrl?: string;
  onSaveSignature: (dataUrl: string) => void;
  onClearSignature: () => void;
  disabled?: boolean;
}

export const SignaturePad: React.FC<SignaturePadProps> = ({
  label = 'ลายเซ็นผู้ตรวจสอบ (Operator Sign-off)',
  signatureDataUrl,
  onSaveSignature,
  onClearSignature,
  disabled = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Initialize canvas with proper scale
  const setupCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#1d4ed8'; // Royal blue stroke for high-contrast light theme
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    setupCanvas();
    const handleResize = () => {
      // Re-setup on resize
      setupCanvas();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getCanvasCoords = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled || signatureDataUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.setPointerCapture(e.pointerId);
    setIsDrawing(true);
    const coords = getCanvasCoords(e);
    lastPointRef.current = coords;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(coords.x, coords.y, 1.25, 0, Math.PI * 2);
      ctx.fillStyle = '#1d4ed8';
      ctx.fill();
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing || disabled || signatureDataUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx || !lastPointRef.current) return;

    const currentPoint = getCanvasCoords(e);

    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(currentPoint.x, currentPoint.y);
    ctx.stroke();

    lastPointRef.current = currentPoint;
    setHasDrawn(true);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.releasePointerCapture(e.pointerId);
    }
    setIsDrawing(false);
    lastPointRef.current = null;

    // Automatically export signature if drawn
    if (canvas && hasDrawn) {
      const dataUrl = canvas.toDataURL('image/png');
      onSaveSignature(dataUrl);
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setHasDrawn(false);
    onClearSignature();
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-xs">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
          <Edit3 className="w-4 h-4 text-blue-600" />
          {label}
        </label>
        {signatureDataUrl && (
          <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            ลงชื่อเรียบร้อย
          </span>
        )}
      </div>

      {signatureDataUrl ? (
        <div className="relative border-2 border-emerald-300 rounded-xl bg-emerald-50/40 p-2 flex items-center justify-between">
          <img
            src={signatureDataUrl}
            alt="Saved signature"
            className="h-24 max-w-full object-contain mx-auto"
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-3 px-3 py-1.5 rounded-lg bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold flex items-center gap-1.5 border border-slate-300 min-h-[40px] transition-colors shadow-2xs"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              เซ็นใหม่
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative border-2 border-dashed border-slate-300 hover:border-slate-400 rounded-xl bg-slate-50 overflow-hidden touch-none">
            <canvas
              ref={canvasRef}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              className="w-full h-32 cursor-crosshair block"
            />
            {!hasDrawn && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-xs font-medium">
                ใช้นิ้วหรือปากกาสไตลัสลงลายมือชื่อที่นี่...
              </div>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={handleClear}
              disabled={!hasDrawn || disabled}
              className="px-3.5 py-2 rounded-lg bg-white hover:bg-slate-100 text-slate-700 text-xs font-semibold flex items-center gap-1.5 border border-slate-300 disabled:opacity-40 min-h-[44px] transition-all select-none active:scale-95 shadow-2xs"
            >
              <RotateCcw className="w-4 h-4" />
              ล้างลายเซ็น
            </button>

            {hasDrawn && (
              <button
                type="button"
                onClick={() => {
                  const canvas = canvasRef.current;
                  if (canvas) {
                    onSaveSignature(canvas.toDataURL('image/png'));
                  }
                }}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold flex items-center gap-1.5 min-h-[44px] shadow-sm select-none active:scale-95"
              >
                <Check className="w-4 h-4" />
                ยืนยันลายเซ็น
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
